<template>
    <div class="space-y-6">
        <!-- Score Hero Card -->
        <div class="bg-gradient-to-r from-pink-500 to-purple-600 rounded-lg shadow-xl p-6 sm:p-8 text-white">
            <div class="text-center">
                <div class="text-sm sm:text-base opacity-90 mb-2">Your Impact Score</div>
                <div class="text-5xl sm:text-6xl md:text-7xl font-bold mb-4">{{ formattedScore }}</div>
                <div class="flex items-center justify-center gap-4 mb-4">
                    <div class="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                        <div class="text-2xl sm:text-3xl font-bold">#{{ rankData.rank }}</div>
                        <div class="text-xs opacity-90">of {{ rankData.total }}</div>
                    </div>
                    <div class="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                        <div class="text-2xl sm:text-3xl font-bold">{{ rankData.percentile }}%</div>
                        <div class="text-xs opacity-90">Percentile</div>
                    </div>
                </div>
                <p class="text-sm sm:text-base opacity-95">
                    {{ performanceMessage }}
                </p>
            </div>
        </div>

        <!-- Educational Section: Why Donor Count Matters -->
        <div class="bg-gradient-to-r from-pink-50 to-purple-50 border-l-4 border-pink-500 rounded-lg p-4 sm:p-6">
            <h2 class="text-lg sm:text-xl font-bold text-gray-900 mb-3 flex items-center" style="font-family: 'Red Hat Text', sans-serif;">
                <span class="text-2xl mr-2">🎗️</span>
                Every Donor is a Research Advocate
            </h2>
            <div class="space-y-3 text-sm sm:text-base text-gray-700">
                <p class="text-base sm:text-lg font-medium text-gray-900">
                    Your impact goes far beyond the dollars you raise.
                </p>
                <p>
                    When someone donates to your campaign, they become part of our research community. They receive updates about breakthrough discoveries, opportunities to participate in clinical trials, and ways to share life-saving information with their networks.
                </p>
                <p>
                    <strong>Think of it this way:</strong> A campaign with 50 donors at $50 each creates 50 ambassadors for breast cancer research. That's 50 people who might volunteer for a study, share our mission with their community, or inspire the next major donor. Those connections are priceless.
                </p>
                <p class="text-pink-700 font-medium">
                    That's why your donor count is the biggest factor in your Impact Score—because each person you engage could be the key to our next research breakthrough.
                </p>
            </div>
        </div>

        <!-- Metrics Grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <!-- Donor Count Metric -->
            <div class="bg-white rounded-lg shadow p-4 sm:p-6">
                <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center">
                        <div class="text-3xl mr-3">👥</div>
                        <div>
                            <h3 class="font-bold text-gray-900 text-sm sm:text-base">Donor Count</h3>
                            <p class="text-xs text-gray-600">Unique supporters</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-2xl sm:text-3xl font-bold" style="color: #da1884;">{{ formatNumber(breakdown.donor_count.raw_value) }}</div>
                    </div>
                </div>
                <div class="mb-2">
                    <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Percentile Rank</span>
                        <span class="font-semibold">{{ breakdown.donor_count.percentile }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                        <div class="rounded-full h-3"
                             :style="{ width: breakdown.donor_count.percentile + '%', backgroundColor: '#da1884' }">
                        </div>
                    </div>
                </div>
                <div :class="`flex items-center mt-3 p-2 rounded ${breakdown.donor_count.emoji_feedback.color === 'green' ? 'bg-green-50' : breakdown.donor_count.emoji_feedback.color === 'blue' ? 'bg-blue-50' : 'bg-yellow-50'}`">
                    <span class="text-lg mr-2">{{ breakdown.donor_count.emoji_feedback.emoji }}</span>
                    <span :class="`text-xs sm:text-sm font-medium ${breakdown.donor_count.emoji_feedback.color === 'green' ? 'text-green-800' : breakdown.donor_count.emoji_feedback.color === 'blue' ? 'text-blue-800' : 'text-yellow-800'}`">
                        {{ breakdown.donor_count.emoji_feedback.message }}
                    </span>
                </div>
            </div>

            <!-- Fundraising Metric -->
            <div class="bg-white rounded-lg shadow p-4 sm:p-6">
                <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center">
                        <div class="text-3xl mr-3">💰</div>
                        <div>
                            <h3 class="font-bold text-gray-900 text-sm sm:text-base">Fundraising</h3>
                            <p class="text-xs text-gray-600">Total raised</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-xl sm:text-2xl font-bold text-green-600">${{ formatNumber(breakdown.fundraising.raw_value) }}</div>
                    </div>
                </div>
                <div class="mb-2">
                    <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Percentile Rank</span>
                        <span class="font-semibold">{{ breakdown.fundraising.percentile }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                        <div class="bg-green-600 rounded-full h-3"
                             :style="{ width: breakdown.fundraising.percentile + '%' }">
                        </div>
                    </div>
                </div>
                <div :class="`flex items-center mt-3 p-2 rounded ${breakdown.fundraising.emoji_feedback.color === 'green' ? 'bg-green-50' : breakdown.fundraising.emoji_feedback.color === 'blue' ? 'bg-blue-50' : 'bg-yellow-50'}`">
                    <span class="text-lg mr-2">{{ breakdown.fundraising.emoji_feedback.emoji }}</span>
                    <span :class="`text-xs sm:text-sm font-medium ${breakdown.fundraising.emoji_feedback.color === 'green' ? 'text-green-800' : breakdown.fundraising.emoji_feedback.color === 'blue' ? 'text-blue-800' : 'text-yellow-800'}`">
                        {{ breakdown.fundraising.emoji_feedback.message }}
                    </span>
                </div>
            </div>

            <!-- Conversion Rate Metric -->
            <div class="bg-white rounded-lg shadow p-4 sm:p-6">
                <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center">
                        <div class="text-3xl mr-3">🎯</div>
                        <div>
                            <h3 class="font-bold text-gray-900 text-sm sm:text-base">Conversion</h3>
                            <p class="text-xs text-gray-600">Visitors who donated</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-2xl sm:text-3xl font-bold text-yellow-600">{{ breakdown.conversion.raw_value }}%</div>
                    </div>
                </div>
                <div class="mb-2">
                    <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Percentile Rank</span>
                        <span class="font-semibold">{{ breakdown.conversion.percentile }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                        <div class="bg-yellow-500 rounded-full h-3"
                             :style="{ width: breakdown.conversion.percentile + '%' }">
                        </div>
                    </div>
                </div>
                <div :class="`flex items-center mt-3 p-2 rounded ${breakdown.conversion.emoji_feedback.color === 'green' ? 'bg-green-50' : breakdown.conversion.emoji_feedback.color === 'blue' ? 'bg-blue-50' : 'bg-yellow-50'}`">
                    <span class="text-lg mr-2">{{ breakdown.conversion.emoji_feedback.emoji }}</span>
                    <span :class="`text-xs sm:text-sm font-medium ${breakdown.conversion.emoji_feedback.color === 'green' ? 'text-green-800' : breakdown.conversion.emoji_feedback.color === 'blue' ? 'text-blue-800' : 'text-yellow-800'}`">
                        {{ breakdown.conversion.emoji_feedback.message }}
                    </span>
                </div>
            </div>

            <!-- Views Metric -->
            <div class="bg-white rounded-lg shadow p-4 sm:p-6">
                <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center">
                        <div class="text-3xl mr-3">👁️</div>
                        <div>
                            <h3 class="font-bold text-gray-900 text-sm sm:text-base">Unique Views</h3>
                            <p class="text-xs text-gray-600">Campaign visibility</p>
                        </div>
                    </div>
                    <div class="text-right">
                        <div class="text-2xl sm:text-3xl font-bold text-purple-600">{{ formatNumber(breakdown.views.raw_value) }}</div>
                    </div>
                </div>
                <div class="mb-2">
                    <div class="flex justify-between text-xs text-gray-600 mb-1">
                        <span>Percentile Rank</span>
                        <span class="font-semibold">{{ breakdown.views.percentile }}%</span>
                    </div>
                    <div class="w-full bg-gray-200 rounded-full h-3">
                        <div class="bg-purple-600 rounded-full h-3"
                             :style="{ width: breakdown.views.percentile + '%' }">
                        </div>
                    </div>
                </div>
                <div :class="`flex items-center mt-3 p-2 rounded ${breakdown.views.emoji_feedback.color === 'green' ? 'bg-green-50' : breakdown.views.emoji_feedback.color === 'blue' ? 'bg-blue-50' : 'bg-yellow-50'}`">
                    <span class="text-lg mr-2">{{ breakdown.views.emoji_feedback.emoji }}</span>
                    <span :class="`text-xs sm:text-sm font-medium ${breakdown.views.emoji_feedback.color === 'green' ? 'text-green-800' : breakdown.views.emoji_feedback.color === 'blue' ? 'text-blue-800' : 'text-yellow-800'}`">
                        {{ breakdown.views.emoji_feedback.message }}
                    </span>
                </div>
            </div>
        </div>

        <!-- Achievements Section -->
        <div class="bg-white rounded-lg shadow">
            <div class="px-4 sm:px-6 py-4 border-b border-gray-200">
                <h2 class="text-lg sm:text-xl font-bold text-gray-900 flex items-center" style="font-family: 'Red Hat Text', sans-serif;">
                    <span class="text-2xl mr-2">🏆</span>
                    Your Achievements
                    <span class="ml-3 px-3 py-1 bg-pink-100 text-pink-800 rounded-full text-sm font-medium">
                        {{ badges.unlocked.length }} of {{ badges.unlocked.length + badges.in_progress.length }} Unlocked
                    </span>
                </h2>
            </div>
            <div class="px-4 sm:px-6 py-6">
                <!-- Unlocked Badges -->
                <div v-if="badges.unlocked.length > 0" class="mb-6">
                    <h3 class="text-md font-semibold text-gray-900 mb-3">Unlocked</h3>
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        <div v-for="(badge, index) in badges.unlocked" :key="`unlocked-${index}`"
                             class="text-center p-3 sm:p-4 rounded-lg shadow-md transform hover:scale-105 transition-transform bg-gradient-to-br"
                             :class="badge.gradient">
                            <div class="text-3xl sm:text-4xl mb-2">{{ badge.icon }}</div>
                            <div class="text-sm font-bold text-gray-900">{{ badge.title }}</div>
                            <div class="text-xs text-gray-800 mt-1">{{ badge.description }}</div>
                        </div>
                    </div>
                </div>

                <!-- In Progress Badges -->
                <div v-if="badges.in_progress.length > 0">
                    <h3 class="text-md font-semibold text-gray-900 mb-3">In Progress</h3>
                    <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        <div v-for="(badge, index) in badges.in_progress" :key="`progress-${index}`"
                             class="text-center p-3 sm:p-4 bg-gray-100 rounded-lg shadow">
                            <div class="text-3xl sm:text-4xl mb-2 opacity-50">{{ badge.icon }}</div>
                            <div class="text-sm font-bold text-gray-700">{{ badge.title }}</div>
                            <div class="text-xs text-gray-600 mt-1">{{ badge.description }}</div>
                            <div class="mt-2">
                                <div class="w-full bg-gray-300 rounded-full h-2">
                                    <div class="bg-pink-500 rounded-full h-2"
                                         :style="{ width: badge.progress + '%' }">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CTA Section -->
        <div class="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-6 text-center border-2 border-cyan-200">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Keep Building Your Impact!</h3>
            <p class="text-gray-700 mb-4">Continue sharing your campaign to unlock more achievements and climb the leaderboard.</p>
            <button @click="$emit('open-share-modal')"
                    class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold rounded-lg hover:from-pink-600 hover:to-pink-700 transition-all transform hover:scale-105">
                <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"></path>
                </svg>
                Share Your Campaign
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'YourCampaignTab',

    props: {
        campaign: {
            type: Object,
            required: true
        },
        breakdown: {
            type: Object,
            required: true
        },
        cohortStats: {
            type: Object,
            required: true
        },
        rankData: {
            type: Object,
            required: true
        },
        badges: {
            type: Object,
            required: true
        }
    },

    computed: {
        formattedScore() {
            return Number(this.campaign.impact_score).toFixed(1)
        },
        grade() {
            const score = this.campaign.impact_score
            if (score >= 90) return 'A'
            if (score >= 80) return 'B'
            if (score >= 70) return 'C'
            if (score >= 60) return 'D'
            return 'F'
        },
        performanceMessage() {
            const { rank, total, percentile } = this.rankData

            if (rank === 1) return "🎉 Incredible! You're #1 in your foundation!"
            if (rank <= 3) return `🏆 Outstanding! You're in the top 3 campaigns.`
            if (percentile >= 90) return `⭐ Exceptional! You're in the top 10%.`
            if (percentile >= 75) return `🌟 Great job! You're in the top 25%.`
            if (percentile >= 50) return `✨ Good progress! You're performing above average.`
            return `💪 Keep building! Every donor makes a difference.`
        }
    },

    methods: {
        formatNumber(num) {
            return Number(num).toLocaleString()
        }
    }
}
</script>

<style scoped>
/* Additional styles if needed */
</style>
